import React from "react";
import { Repeat } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RecurrenceType } from "../../features/sessions/models/RecurrenceType";
import { buildRecurrenceSummary } from "../../features/sessions/utils/recurrenceSummary";

/** The recurrence detail fields the instructor can configure for a repeating session. */
export interface RecurrenceConfig {
  recurrenceType: RecurrenceType;
  recurrenceDays?: string[];
  recurrenceDaysNumber?: number;
  recurrenceDateEnd?: Date;
  finishAfterOccurrences?: number;
}

interface RecurrenceOptionsProps {
  config: RecurrenceConfig;
  onChange: (config: RecurrenceConfig) => void;
  className?: string;
}

const WEEK_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const CADENCES: { type: RecurrenceType; labelKey: string }[] = [
  { type: RecurrenceType.BiWeekly, labelKey: "sessionForm.everyTwoWeeks" },
  { type: RecurrenceType.EveryThirdWeek, labelKey: "sessionForm.everyThreeWeeks" },
  { type: RecurrenceType.EveryFourthWeek, labelKey: "sessionForm.everyFourWeeks" },
  { type: RecurrenceType.DailyWeekend, labelKey: "sessionForm.everyWeekendDay" },
  { type: RecurrenceType.MonthlyFirstDay, labelKey: "sessionForm.firstWeekdayOfMonth" },
];

const usesWeekDays = (type: RecurrenceType): boolean =>
  type === RecurrenceType.Weekly ||
  type === RecurrenceType.BiWeekly ||
  type === RecurrenceType.EveryThirdWeek ||
  type === RecurrenceType.EveryFourthWeek;

const isCustomCadence = (type: RecurrenceType): boolean =>
  CADENCES.some((c) => c.type === type);

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * Detail controls for a repeating session: which weekdays (weekly cadences), which
 * day of the month (monthly), the custom cadence, and when the series ends
 * (never / on a date / after N occurrences).
 */
const RecurrenceOptions: React.FC<RecurrenceOptionsProps> = ({
  config,
  onChange,
  className = "",
}) => {
  const { t } = useTranslation();
  const { recurrenceType, recurrenceDays, recurrenceDaysNumber } = config;

  const endMode = config.recurrenceDateEnd
    ? "onDate"
    : config.finishAfterOccurrences
      ? "afterCount"
      : "never";

  const toggleDay = (day: string) => {
    const current = recurrenceDays ?? [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    // Keep at least one day selected so the series never silently disappears.
    if (next.length === 0) return;
    onChange({ ...config, recurrenceDays: WEEK_DAYS.filter((d) => next.includes(d)) });
  };

  const setEndMode = (mode: "never" | "onDate" | "afterCount") => {
    const defaultEnd = new Date();
    defaultEnd.setMonth(defaultEnd.getMonth() + 3);
    onChange({
      ...config,
      recurrenceDateEnd: mode === "onDate" ? (config.recurrenceDateEnd ?? defaultEnd) : undefined,
      finishAfterOccurrences:
        mode === "afterCount" ? (config.finishAfterOccurrences ?? 10) : undefined,
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {isCustomCadence(recurrenceType) && (
        <div className="space-y-2">
          <p className="font-bold">{t("sessionForm.customCadence")}</p>
          <div className="flex max-w-[476px] flex-wrap gap-4">
            {CADENCES.map((cadence) => (
              <label key={cadence.type} className="flex cursor-pointer">
                <input
                  type="radio"
                  name="custom-cadence"
                  className="peer hidden"
                  checked={recurrenceType === cadence.type}
                  onChange={() => onChange({ ...config, recurrenceType: cadence.type })}
                />
                <span className="checkbox-group-item py-0">{t(cadence.labelKey)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {(usesWeekDays(recurrenceType) || recurrenceType === RecurrenceType.MonthlyFirstDay) && (
        <div className="space-y-2">
          <p className="font-bold">
            {recurrenceType === RecurrenceType.MonthlyFirstDay
              ? t("sessionForm.onFirstWeekday")
              : t("sessionForm.onDays")}
          </p>
          <div className="flex flex-wrap gap-2">
            {WEEK_DAYS.map((day) => {
              const selected =
                recurrenceType === RecurrenceType.MonthlyFirstDay
                  ? recurrenceDays?.[0] === day
                  : (recurrenceDays ?? []).includes(day);
              return (
                <label key={day} className="flex cursor-pointer">
                  <input
                    type={recurrenceType === RecurrenceType.MonthlyFirstDay ? "radio" : "checkbox"}
                    name="recurrence-days"
                    className="peer hidden"
                    checked={selected ?? false}
                    onChange={() =>
                      recurrenceType === RecurrenceType.MonthlyFirstDay
                        ? onChange({ ...config, recurrenceDays: [day] })
                        : toggleDay(day)
                    }
                  />
                  <span className="checkbox-group-item py-0">
                    {t(`sessionForm.weekday${day}`)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {recurrenceType === RecurrenceType.MonthlyDayNumber && (
        <div className="space-y-2">
          <p className="font-bold">{t("sessionForm.onDayOfMonth")}</p>
          <input
            type="number"
            min={1}
            max={31}
            className="input w-24"
            value={recurrenceDaysNumber ?? ""}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              onChange({
                ...config,
                recurrenceDaysNumber:
                  isNaN(value) ? undefined : Math.min(31, Math.max(1, value)),
              });
            }}
          />
        </div>
      )}

      <div className="space-y-2">
        <p className="font-bold">{t("sessionForm.seriesEnds")}</p>
        <div className="flex max-w-[476px] flex-wrap items-center gap-4">
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="series-end"
              className="peer hidden"
              checked={endMode === "never"}
              onChange={() => setEndMode("never")}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.endsNever")}</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="series-end"
              className="peer hidden"
              checked={endMode === "onDate"}
              onChange={() => setEndMode("onDate")}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.endsOnDate")}</span>
          </label>
          <label className="flex cursor-pointer">
            <input
              type="radio"
              name="series-end"
              className="peer hidden"
              checked={endMode === "afterCount"}
              onChange={() => setEndMode("afterCount")}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.endsAfter")}</span>
          </label>
        </div>

        {endMode === "onDate" && (
          <input
            type="date"
            className="input w-44"
            value={config.recurrenceDateEnd ? isoDate(config.recurrenceDateEnd) : ""}
            onChange={(e) =>
              onChange({
                ...config,
                recurrenceDateEnd: e.target.value ? new Date(e.target.value) : undefined,
              })
            }
          />
        )}

        {endMode === "afterCount" && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={365}
              className="input w-24"
              value={config.finishAfterOccurrences ?? ""}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                onChange({
                  ...config,
                  finishAfterOccurrences:
                    isNaN(value) ? undefined : Math.min(365, Math.max(1, value)),
                });
              }}
            />
            <span className="text-sm text-grey-30 dark:text-grey-70">
              {t("sessionForm.occurrencesSuffix")}
            </span>
          </div>
        )}
      </div>

      {/* Live confirmation of what was configured, in plain language. */}
      {(() => {
        const summary = buildRecurrenceSummary(config, t);
        if (!summary) return null;
        return (
          <p className="flex items-center gap-2 rounded-xl bg-grey-95 px-3 py-2 text-sm font-medium text-grey-20 dark:bg-primary-20 dark:text-grey-90">
            <Repeat size={15} className="shrink-0 text-primary-40 dark:text-primary-90" />
            {t("sessionForm.summary.repeats", { pattern: summary })}
          </p>
        );
      })()}
    </div>
  );
};

export default RecurrenceOptions;
